const joinPosix = function () {
    const segments = Array.prototype.slice.call(arguments);
    let path = '';
    let isAbsolute = false;

    segments.forEach(function (segment) {
        if (!segment) return;

        if (typeof segment !== 'string') {
            segment = String(segment);
        }

        if (path === '') {
            if (segment.startsWith('/')) {
                isAbsolute = true;
            }
            const trimmed = segment.replace(/\/+$/g, '');
            path = trimmed;
            if (path === '') {
                path = '';
            }
        } else {
            const cleaned = segment.replace(/^\/+/g, '').replace(/\/+$/g, '');
            if (cleaned) {
                if (path.endsWith('/')) {
                    path = path.replace(/\/+$/g, '') + '/' + cleaned;
                } else {
                    path += '/' + cleaned;
                }
            }
        }
    });

    if (isAbsolute && path && !path.startsWith('/')) {
        path = '/' + path;
    }

    if (!path && isAbsolute) {
        return '/';
    }

    return path;
};

const env = (typeof process !== 'undefined' && process && process.env) ? process.env : {};

const cdnUrl = function () {
    const args = Array.prototype.slice.call(arguments).filter(arg => !!arg);

    if(args.length > 0 && args[0].includes('://')) return joinPosix.apply(this, args);

    args.unshift('/');

    // Forcefully using `posix` here to ensure we have forward slashes when path joining
    return (env.CLOUD_FRONT_BASE_URL || '') + joinPosix.apply(this, args);
};

const hasOwn = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

const splitUrlSuffix = (value) => {
    const suffixIndex = value.search(/[?#]/);

    if (suffixIndex === -1) {
        return {path: value, suffix: ''};
    }

    return {
        path: value.slice(0, suffixIndex),
        suffix: value.slice(suffixIndex)
    };
};

const parseUrlReference = (value) => {
    const match = value.match(/^(?:([a-z][a-z\d+.-]*:)?\/\/([^/?#]+))([^?#]*)([?#].*)?$/i);
    if (!match) return null;

    return {
        protocol: match[1] || '',
        authority: match[2],
        path: match[3] || '/',
        suffix: match[4] || ''
    };
};

const getConfiguredFirstPartyPath = (value) => {
    const configuredBaseUrl = typeof env.CLOUD_FRONT_BASE_URL === 'string' ? env.CLOUD_FRONT_BASE_URL.trim() : '';
    if (!configuredBaseUrl) return null;

    const configuredBase = parseUrlReference(configuredBaseUrl);
    const imageUrl = parseUrlReference(value);
    if (!configuredBase || !imageUrl) return null;

    if (configuredBase.authority.toLowerCase() !== imageUrl.authority.toLowerCase()) return null;
    if (configuredBase.protocol && imageUrl.protocol && configuredBase.protocol.toLowerCase() !== imageUrl.protocol.toLowerCase()) return null;

    const basePath = configuredBase.path.replace(/\/+$/g, '') || '/';
    const imagePath = imageUrl.path || '/';
    if (basePath !== '/' && imagePath !== basePath && !imagePath.startsWith(`${basePath}/`)) return null;

    const relativePath = basePath === '/' ? imagePath : imagePath.slice(basePath.length) || '/';
    return relativePath + imageUrl.suffix;
};

const canTransformImageUrl = (value) => {
    if (typeof value !== 'string' || !value.trim()) return false;

    const trimmedValue = value.trim();
    const configuredFirstPartyPath = getConfiguredFirstPartyPath(trimmedValue);
    const isAbsoluteOrProtocolRelative = trimmedValue.startsWith('//') || /^[a-z][a-z\d+.-]*:/i.test(trimmedValue);
    if (configuredFirstPartyPath === null && isAbsoluteOrProtocolRelative) return false;

    const imagePath = configuredFirstPartyPath === null
        ? splitUrlSuffix(trimmedValue).path
        : splitUrlSuffix(configuredFirstPartyPath).path;

    return /\.(?:jpe?g|png|webp)(?:\/|$)/i.test(imagePath);
};

const normaliseNumber = (value, maximum) => {
    if (typeof value !== 'number' && typeof value !== 'string') return null;
    if (typeof value === 'string' && !value.trim()) return null;

    const number = Number(value);
    if (!Number.isFinite(number) || number < 0 || (maximum !== undefined && number > maximum)) return null;

    return Math.round(number);
};

const normaliseFormat = (value) => {
    if (typeof value !== 'string') return null;

    const format = value.trim().toLowerCase();
    if (!['webp', 'jpg', 'jpeg', 'png'].includes(format)) return null;

    return format === 'jpeg' ? 'jpg' : format;
};

const getFitInDimensions = (path) => {
    const segments = path.split('/');
    const fitInIndex = segments.findIndex(segment => segment.toLowerCase() === 'fit-in');
    if (fitInIndex === -1 || !segments[fitInIndex + 1]) return null;

    const match = segments[fitInIndex + 1].match(/^(\d+)x(\d+)$/i);
    if (!match) return null;

    return {
        width: Number(match[1]),
        height: Number(match[2])
    };
};

const replaceFitInDimensions = (path, width, height) => {
    const segments = path.split('/');
    const dimensions = `${width}x${height}`;
    const fitInIndex = segments.findIndex(segment => segment.toLowerCase() === 'fit-in');

    if (fitInIndex !== -1) {
        segments[fitInIndex + 1] = dimensions;
    } else {
        const filterIndex = segments.findIndex(segment => /^filters(?::|$)/i.test(segment));
        segments.splice(filterIndex === -1 ? segments.length : filterIndex, 0, 'fit-in', dimensions);
    }

    return segments.join('/');
};

const upsertFilter = (path, filterName, value) => {
    const filterPattern = new RegExp(`^${filterName}\\([^)]*\\)$`, 'i');
    let found = false;

    const segments = path.split('/').reduce((result, segment) => {
        const tokens = segment.split(':');
        const nextTokens = [];

        tokens.forEach(token => {
            if (!filterPattern.test(token)) {
                nextTokens.push(token);
                return;
            }

            if (!found) {
                nextTokens.push(`${filterName}(${value})`);
                found = true;
            }
        });

        if ((nextTokens.length === 0 || (nextTokens.length === 1 && nextTokens[0].toLowerCase() === 'filters')) && tokens.length > 1) {
            return result;
        }

        result.push(nextTokens.join(':'));
        return result;
    }, []);

    if (!found) segments.push(`filters:${filterName}(${value})`);

    return segments.join('/');
};

/**
 * Builds a transformed URL for a local raster image without changing the legacy cdnUrl contract.
 *
 * @param {string} value
 * @param {Object} options
 * @return {string}
 */
const cdnImageUrl = function (value, options) {
    if (typeof value !== 'string' || !value.trim()) return '';

    const trimmedValue = value.trim();
    const configuredFirstPartyPath = getConfiguredFirstPartyPath(trimmedValue);
    const isAbsoluteOrProtocolRelative = trimmedValue.startsWith('//') || /^[a-z][a-z\d+.-]*:/i.test(trimmedValue);
    if (configuredFirstPartyPath === null && isAbsoluteOrProtocolRelative) return trimmedValue;

    const imageOptions = options && options.hash ? options.hash : (options || {});
    const {path: imagePath, suffix} = configuredFirstPartyPath === null
        ? splitUrlSuffix(trimmedValue)
        : splitUrlSuffix(configuredFirstPartyPath);
    const localPath = (imagePath.startsWith('/') ? imagePath : `/${imagePath}`).replace(/\/+$/g, '');
    const isRaster = /\.(?:jpe?g|png|webp)(?:\/|$)/i.test(imagePath);
    const isResized = /^\/resize(?:\/|$)/i.test(localPath);

    if (!isRaster) return cdnUrl(localPath + suffix);

    const requestedWidth = hasOwn(imageOptions, 'width') ? normaliseNumber(imageOptions.width) : null;
    const requestedHeight = hasOwn(imageOptions, 'height') ? normaliseNumber(imageOptions.height) : null;
    const hasRequestedDimensions = requestedWidth !== null || requestedHeight !== null;
    const requestedQuality = hasOwn(imageOptions, 'quality') ? normaliseNumber(imageOptions.quality, 100) : null;
    const requestedFormat = hasOwn(imageOptions, 'format') ? normaliseFormat(imageOptions.format) : null;
    const noUpscale = imageOptions.noUpscale === true || imageOptions.noUpscale === 'true';

    let transformedPath;
    if (isResized) {
        transformedPath = localPath;

        if (hasRequestedDimensions) {
            const existingDimensions = getFitInDimensions(localPath);
            const width = requestedWidth !== null ? requestedWidth : (existingDimensions ? existingDimensions.width : 0);
            const height = requestedHeight !== null ? requestedHeight : (existingDimensions ? existingDimensions.height : 0);
            transformedPath = replaceFitInDimensions(transformedPath, width, height);
        }
    } else {
        const width = requestedWidth === null ? 0 : requestedWidth;
        const height = requestedHeight === null ? 0 : requestedHeight;
        transformedPath = `/resize${localPath}/fit-in/${width}x${height}`;
    }

    if (requestedQuality !== null) {
        transformedPath = upsertFilter(transformedPath, 'quality', requestedQuality);
    }

    if (requestedFormat !== null) {
        transformedPath = upsertFilter(transformedPath, 'format', requestedFormat);
    }

    if (noUpscale) {
        transformedPath = upsertFilter(transformedPath, 'no_upscale', '');
    }

    return cdnUrl(transformedPath + suffix);
};

/**
 * Builds a link that points to the S3 Bucket Public URL. The provided arguments show always be string. These arguments
 * are parsed through to the path join function to handle path concatenation.
 *
 * @return {string}
 */
const relativeBucketUrl = function () {
    const args = Array.prototype.slice.call(arguments).filter(arg => !!arg);
    args.unshift('/');

    return (env.STATIC_CONTENT_CACHE_BASE_URL || '') + joinPosix.apply(this, args);
};

module.exports = {
    vehiclePlaceholderKey: 'images/common/vehicle-placeholder.png',
    relativeBucketUrl,
    cdnUrl,
    cdnImageUrl,
    canTransformImageUrl
};
